import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { useState } from "react";
import SearchBar from "../../components/SearchBar";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import { usePermission } from "../../hooks/usePermission";
import Can from "../../components/Can";
import SwitchToggle from "../../components/SwitchToggle";
import {
  useDeleteStripePlanMutation,
  useGetStripePlansQuery,
  useUpdateStripePlanMutation,
} from "../../service/stripe/api";
import moment from "moment";
import { toast } from "react-toastify";
import DeleteConfirmModal from "../../Modals/DeleteConfirmModal";

const ManagePackages = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("Manage Packages", "isEdit");
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModal, setDeleteModal] = useState({ open: false, id: "" });

  const { data, isLoading } = useGetStripePlansQuery(
    searchTerm ? { search: searchTerm } : {}
  );
  const [updateStripePlan, { isLoading: isUpdating }] =
    useUpdateStripePlanMutation();
  const [deleteStripePlan, { isLoading: isDeleting }] =
    useDeleteStripePlanMutation();

  const rows = data?.data?.data || [];

  const handleToggleActive = async (row: any) => {
    try {
      await updateStripePlan({ id: row._id, isActive: !row.isActive }).unwrap();
      toast.success("Status updated successfully / تم تحديث الحالة بنجاح", { autoClose: 2000 });
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to update status / فشل تحديث الحالة");
    }
  };

  const handleDeleteClick = (id: string) => {
    setDeleteModal({ open: true, id });
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteStripePlan(deleteModal.id).unwrap();
      toast.success("Plan deleted successfully / تم حذف الباقة بنجاح", { autoClose: 2000 });
      setDeleteModal({ open: false, id: "" });
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to delete plan / فشل حذف الباقة");
    }
  };

  return (
    <>
      <CommonBreadcrumb
        title="Package Management / إدارة الباقات"
        breadcrumbItems={[
          { label: "Dashboard / لوحة المعلومات", onClick: () => navigate("/dashboard") },
          { label: "Package Management / إدارة الباقات" },
        ]}
      />

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search Packages... / ابحث عن الباقات..."
            />
          </div>
          <div className="right_s">
            <Can module="Manage Packages" action="isAdd" isDisabledMode={true}>
              <Button
                className="btn btn_primary"
                onClick={() => navigate("/manage-packages/add")}
              >
                <AddIcon /> Add Package / إضافة باقة
              </Button>
            </Can>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sr. No. / الرقم التسلسلي</TableCell>
                <TableCell>Package Name / اسم الباقة</TableCell>
                <TableCell>Price / السعر</TableCell>
                <TableCell>Interval / المدة</TableCell>
                <TableCell>Disable/Enable / تعطيل/تفعيل</TableCell>
                <TableCell>Created Date / تاريخ الإنشاء</TableCell>
                <TableCell>Actions / الإجراءات</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Loading... / جارٍ التحميل...
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No packages found. / لا توجد باقات.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row: any, index: number) => (
                  <TableRow hover key={row._id}>
                    <TableCell>{index + 1}</TableCell>

                    <TableCell>
                      <div className="user_block">
                        <p>
                          <b>{row.name}</b>
                        </p>
                      </div>
                    </TableCell>

                    <TableCell>
                      {row.currency?.toUpperCase()} {row.amount}
                    </TableCell>

                    <TableCell>{row.interval}</TableCell>

                    <TableCell>
                      {canEdit ? (
                        <SwitchToggle
                          checked={row.isActive}
                          onChange={() => handleToggleActive(row)}
                          disabled={isUpdating}
                        />
                      ) : (
                        <span className={`status_btn ${row.isActive ? "c_dark_info" : "c_dark_warning"}`}>
                          {row.isActive ? "Enabled / مفعل" : "Disabled / معطل"}
                        </span>
                      )}
                    </TableCell>

                    <TableCell>
                      {moment(row.createdAt).format("DD-MMM-YYYY")}
                    </TableCell>

                    <TableCell>
                      <div className="table_actions v2">
                        <Tooltip title="View / عرض" arrow>
                          <IconButton
                            onClick={() =>
                              navigate(`/manage-packages/detail/${row._id}`)
                            }
                          >
                            <img src="/static/images/eye_icon.svg" alt="View / عرض" />
                          </IconButton>
                        </Tooltip>

                        <Can module="Manage Packages" action="isEdit" isDisabledMode={true}>
                          <Tooltip title="Edit / تعديل" arrow>
                            <IconButton
                              onClick={() =>
                                navigate(`/manage-packages/edit/${row._id}`)
                              }
                            >
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt="Edit / تعديل"
                              />
                            </IconButton>
                          </Tooltip>
                        </Can>

                        <Can module="Manage Packages" action="isDelete" isDisabledMode={true}>
                          <Tooltip title="Delete / حذف" arrow>
                            <IconButton
                              onClick={() => handleDeleteClick(row._id)}
                            >
                              <img
                                src="/static/images/trash2_icon.svg"
                                alt="Delete / حذف"
                              />
                            </IconButton>
                          </Tooltip>
                        </Can>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <DeleteConfirmModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: "" })}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        message="Are you sure you want to delete this package? / هل أنت متأكد أنك تريد حذف هذه الباقة؟"
      />
    </>
  );
};

export default ManagePackages;
